import {useEffect, useState, memo} from 'react';
import {SelectedAccount, OrderRecord, TraderDBAccount} from '@qep/types/src/index';
import OrderDetailsModal, {type OrderDetailsDraft} from '../../OrderDetailsModal/OrderDetailsModal';
import {accounttoAccountSelectorValue} from '../utils';
import {isValidAccountText, notifyInvalidAccountText} from '../accountTextValidation';

interface AccountSelectModalProps {
  accounts: TraderDBAccount[];
  leg?: OrderRecord;
  submitted?: boolean;
  show?: boolean;
  onCancel: () => void;
  onContinue: (account: SelectedAccount) => void;
}

/**
 * Creates selected account defaults from an existing leg account or first account fallback.
 */
function resolveInitialAccountSelectorValue(accounts: TraderDBAccount[], leg?: OrderRecord): SelectedAccount | undefined {
  if (accounts.length === 0) {
    return undefined;
  }

  if (leg?.account) {
    const legAccount = leg.account as TraderDBAccount & {
      accountId?: string;
      execCode?: string;
      tradeIntentionId?: string;
      notes?: string[];
    };
    const [tradeIntentionName = '', customText2 = '', customText3 = ''] = legAccount.notes ?? [];
    const account = accounts.find(account => (
      account.id === legAccount.id
      || (
        account.neovestAccount === legAccount.accountId
        && account.code === legAccount.execCode
      )
    )) ?? accounts[0];

    const tradeIntentions = account.tradeIntentions ?? [];
    let tradeIntention;
    if (legAccount.tradeIntentionId) {
      tradeIntention = tradeIntentions.find(ti => ti.id === legAccount.tradeIntentionId);
    } else {
      tradeIntention = tradeIntentions.find(ti => ti.tradeIntentionName === tradeIntentionName);
    }

    return {
      account,
      memo: legAccount.memo,
      tradeIntention,
      customText1: tradeIntentionName === tradeIntention?.tradeIntentionName ? undefined : tradeIntentionName,
      customText2,
      customText3,
    };
  }

  return accounttoAccountSelectorValue(accounts[0]);
}

/**
 * Maps selected account model into shared order details modal draft values.
 */
function mapSelectedAccountToDetails(accountSelectorValue: SelectedAccount): OrderDetailsDraft {
  return {
    memo: accountSelectorValue.memo ?? '',
    customTag1: accountSelectorValue.customText1 ?? accountSelectorValue.tradeIntention?.tradeIntentionName ?? '',
    customTag2: accountSelectorValue.customText2 ?? '',
    customTag3: accountSelectorValue.customText3 ?? '',
  };
}

/**
 * Converts shared order details values back into the selected account shape expected by consumers.
 */
function mapDetailsToSelectedAccount(currentValue: SelectedAccount, details: OrderDetailsDraft): SelectedAccount {
  const matchedTradeIntention = (currentValue.account.tradeIntentions ?? []).find(tradeIntention => tradeIntention.tradeIntentionName === details.customTag1);

  return {
    ...currentValue,
    memo: details.memo ?? '',
    tradeIntention: matchedTradeIntention,
    customText1: matchedTradeIntention ? undefined : (details.customTag1 ?? ''),
    customText2: details.customTag2 ?? '',
    customText3: details.customTag3 ?? '',
  };
}

/**
 * Validates free text fields before submitting account detail edits.
 */
function validateDetails(details: OrderDetailsDraft) {
  for (const value of [details.memo, details.customTag1, details.customTag2]) {
    if (typeof value === 'string' && !isValidAccountText(value)) {
      notifyInvalidAccountText(value);
      return false;
    }
  }

  return true;
}

export default memo(function AccountSelectModal({
  accounts = [],
  leg = undefined,
  submitted = false,
  show = false,
  onCancel = () => {},
  onContinue = () => {},
}: AccountSelectModalProps) {
  const [accountSelectorValue, setAccountSelectorValue] = useState<SelectedAccount | undefined>();

  /**
   * Handles modal save and forwards selected account updates when valid.
   */
  const handleSave = (details: OrderDetailsDraft) => {
    if (submitted || !accountSelectorValue) {
      return;
    }

    if (!validateDetails(details)) {
      return;
    }

    const nextSelectedAccount = mapDetailsToSelectedAccount(accountSelectorValue, details);
    onContinue(nextSelectedAccount);
  };

  useEffect(() => {
    if (!show) {
      return;
    }

    setAccountSelectorValue(resolveInitialAccountSelectorValue(accounts, leg));
  }, [accounts, leg, show]);

  if (!show || !accountSelectorValue) {
    return null;
  }

  return (
    <OrderDetailsModal
      initialDetails={mapSelectedAccountToDetails(accountSelectorValue)}
      customTag1Options={(accountSelectorValue.account.tradeIntentions ?? []).map(ti => ti.tradeIntentionName)}
      onClose={onCancel}
      onSave={handleSave}
    />
  );
});
